import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import TopupPurchaseDialog from "../../components/TopupPurchaseDialog";
import getSymbolFromCurrency from "currency-symbol-map";
import {
  topupAppliesToLabel,
  topupStatusMeta,
  formatAllowanceLimit,
  formatAllowanceBreakdown,
  allowanceSupportsTopup,
  sortedAllowanceEntries,
  formatAllowanceGrant,
} from "../../constants/planConstants";
import { formatPrice } from "../../utils/planHelpers";
import { formatDate } from "../../utils/DateFormat";
import styles from "./style";

const HISTORY_HEADERS = [
  "Pack",
  "Allowances",
  "Qty",
  "Amount",
  "Purchased",
  "Expires",
  "Status",
];

const renderAllowanceList = (allowances, textSx) => {
  const entries = sortedAllowanceEntries(allowances);
  if (entries.length === 0) return "—";
  return entries.map(([key, value]) => {
    const { label, display } = formatAllowanceGrant(key, value);
    return (
      <Typography key={key} sx={textSx}>
        {label}: {display}
      </Typography>
    );
  });
};

// Amount and currency are rendered exactly as the backend supplied them — no
// conversion, no recalculation.
const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const code = String(currency || "").toUpperCase();
  const symbol = code ? getSymbolFromCurrency(code) : "";
  const value = formatPrice(amount);
  if (symbol) return `${symbol}${value}`;
  return code ? `${code} ${value}` : value;
};

const packQuantityText = (pack) => {
  if (pack?.packType !== "per_unit") {
    // A fixed pack has no bundle-size field of its own — what it grants is
    // already listed above via the allowances rows.
    return "";
  }
  const min = pack?.minQuantity;
  const max = pack?.maxQuantity;
  if (min === undefined && max === undefined) return "Choose how many you need";
  return `Choose between ${min ?? "—"} and ${max ?? "—"}`;
};

// Maps the authoritative purchase status to how the outcome banner reads.
// `pending` is explicitly not a failure.
export const outcomeSeverity = (status) => {
  if (status === "active") return "success";
  if (status === "failed") return "error";
  if (status === "pending") return "info";
  return "warning";
};

export const outcomeMessage = (purchase) => {
  const name = purchase?.packNameSnapshot || "Your topup";
  switch (purchase?.status) {
    case "active":
      return `${name} is active. Your allowance has been updated.`;
    case "pending":
      return "Your payment is still being processed. Refresh in a moment to see the latest status.";
    case "failed":
      return `Payment for ${name} did not succeed. You can try again.`;
    case "cancelled":
      return `${name} was cancelled.`;
    case "expired":
      return `${name} has expired.`;
    default:
      return "Your payment is still being processed. Refresh in a moment to see the latest status.";
  }
};

const TopupsPage = ({
  usage = null,
  usageLoading = false,
  usageError = "",
  available = [],
  availableLoading = false,
  availableError = "",
  purchases = [],
  purchasesLoading = false,
  purchasesError = "",
  employeeCount = null,
  numeratorAllowance = "activeEmployees",
  selectedPack = null,
  quote = null,
  checkoutSubmitting = false,
  checkoutError = "",
  reconciling = false,
  reconciledPurchase = null,
  reconcileNotice = "",
  onSelectPack,
  onClosePurchase,
  onStartCheckout,
  onProceedToPayment,
  onRefreshStatus,
  onDismissOutcome,
  onBack,
}) => {
  const allowanceEntries = usage?.allowances
    ? Object.entries(usage.allowances)
    : [];

  const renderAllowanceValue = (key, allowance) => {
    const limit = formatAllowanceLimit(allowance);

    // Only the allowance with a real count surface gets a numerator. Everything
    // else shows the limit alone rather than an invented or zeroed number.
    const hasNumerator =
      key === numeratorAllowance &&
      employeeCount !== null &&
      employeeCount !== undefined &&
      allowanceSupportsTopup(allowance) &&
      limit !== "—";

    if (hasNumerator) return `${employeeCount} / ${limit}`;
    return limit;
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Box>
          <Typography sx={styles.title}>Topups &amp; Usage</Typography>
          <Typography sx={styles.subtitle}>
            {usage?.planName
              ? `Your allowances on the ${usage.planName} plan.`
              : "Your plan allowances and add-ons."}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onBack} sx={styles.backButton}>
          Back to Profile
        </Button>
      </Box>

      {/* Payment outcome. Rendered only from the authoritative purchase record
          returned by confirm plus the refreshed history — never from the
          advisory status query parameter. */}
      {reconciling && (
        <Alert severity="info" sx={styles.errorAlert} data-testid="reconciling">
          Checking your payment status…
        </Alert>
      )}

      {!reconciling && reconciledPurchase && (
        <Alert
          severity={outcomeSeverity(reconciledPurchase.status)}
          sx={styles.errorAlert}
          onClose={onDismissOutcome}
          data-testid="purchase-outcome"
        >
          {outcomeMessage(reconciledPurchase)}
        </Alert>
      )}

      {!reconciling && !reconciledPurchase && reconcileNotice && (
        <Alert
          severity="info"
          sx={styles.errorAlert}
          data-testid="purchase-processing"
          action={
            <Button color="inherit" size="small" onClick={onRefreshStatus}>
              Refresh
            </Button>
          }
        >
          {reconcileNotice}
        </Alert>
      )}

      {/* ---------------- Usage / limits ---------------- */}
      <Box sx={styles.card}>
        <Typography sx={styles.sectionTitle}>Your allowances</Typography>
        <Typography sx={styles.sectionSubtitle}>
          Plan allowance plus any active topups.
        </Typography>

        {usageError && (
          <Alert severity="error" sx={styles.errorAlert}>
            {usageError}
          </Alert>
        )}

        {usageLoading ? (
          <Box sx={styles.loadingBox}>
            <CircularProgress size={24} />
          </Box>
        ) : allowanceEntries.length === 0 ? (
          <Typography sx={styles.emptyText}>
            {usageError
              ? "Allowances could not be loaded."
              : "No allowances reported for your plan."}
          </Typography>
        ) : (
          allowanceEntries.map(([key, allowance]) => {
            const breakdown = formatAllowanceBreakdown(allowance);
            const showsNumerator =
              key === numeratorAllowance && employeeCount !== null;
            return (
              <Box key={key} sx={styles.allowanceRow} data-testid={`allowance-${key}`}>
                <Box>
                  <Typography sx={styles.allowanceName}>
                    {topupAppliesToLabel(allowance?.appliesTo || key)}
                  </Typography>
                  {breakdown && (
                    <Typography sx={styles.allowanceBreakdown}>{breakdown}</Typography>
                  )}
                  {!showsNumerator && allowanceSupportsTopup(allowance) && (
                    <Typography sx={styles.allowanceBreakdown}>
                      Current usage not available
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={styles.allowanceValue}
                  data-testid={`allowance-value-${key}`}
                >
                  {renderAllowanceValue(key, allowance)}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>

      {/* ---------------- Available add-ons ---------------- */}
      <Box sx={styles.card}>
        <Typography sx={styles.sectionTitle}>Available add-ons</Typography>
        <Typography sx={styles.sectionSubtitle}>
          Packs you can add to your current plan.
        </Typography>

        {availableError && (
          <Alert severity="error" sx={styles.errorAlert}>
            {availableError}
          </Alert>
        )}

        {availableLoading ? (
          <Box sx={styles.loadingBox}>
            <CircularProgress size={24} />
          </Box>
        ) : available.length === 0 ? (
          <Typography sx={styles.emptyText}>
            {availableError
              ? "Add-ons could not be loaded."
              : "No add-ons are available for your plan right now."}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {available.map((pack) => {
              const packId = pack?._id || pack?.id;
              const quantityText = packQuantityText(pack);
              const price = pack?.priceForCountry;
              return (
                <Grid item xs={12} sm={6} md={4} key={packId}>
                  <Box sx={styles.packCard} data-testid={`pack-${packId}`}>
                    <Typography sx={styles.packName}>{pack?.name || "—"}</Typography>
                    {renderAllowanceList(pack?.allowances, styles.packDetail)}
                    {quantityText && (
                      <Typography sx={styles.packDetail}>{quantityText}</Typography>
                    )}
                    <Typography sx={styles.packPrice}>
                      {/* Price comes straight from priceForCountry; a pack with
                          no price for this country is never returned at all. */}
                      {formatAmount(price?.price, price?.currency)}
                      {pack?.packType === "per_unit" ? " per unit" : ""}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={styles.packButton}
                      onClick={() => onSelectPack && onSelectPack(pack)}
                    >
                      Buy
                    </Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* ---------------- Purchase history ---------------- */}
      <Box sx={styles.card}>
        <Typography sx={styles.sectionTitle}>Your topups</Typography>
        <Typography sx={styles.sectionSubtitle}>
          Every topup you have bought, newest first.
        </Typography>

        {purchasesError && (
          <Alert severity="error" sx={styles.errorAlert}>
            {purchasesError}
          </Alert>
        )}

        {purchasesLoading ? (
          <Box sx={styles.loadingBox}>
            <CircularProgress size={24} />
          </Box>
        ) : purchases.length === 0 ? (
          <Typography sx={styles.emptyText}>
            {purchasesError
              ? "Your topups could not be loaded."
              : "You have not bought any topups yet."}
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {HISTORY_HEADERS.map((header) => (
                    <TableCell key={header} sx={styles.tableHeaderCell}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases.map((purchase, index) => {
                  const status = topupStatusMeta(purchase?.status);
                  return (
                    <TableRow key={purchase?._id || purchase?.id || index}>
                      <TableCell sx={styles.tableCell}>
                        {purchase?.packNameSnapshot || "—"}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {renderAllowanceList(purchase?.allowances, { fontSize: "13px" })}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {purchase?.quantity ?? "—"}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {formatAmount(purchase?.amountPaid, purchase?.currency)}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {formatDate(purchase?.purchasedAt)}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {formatDate(purchase?.expiresAt)}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          sx={styles.statusChip}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      <TopupPurchaseDialog
        open={!!selectedPack}
        pack={selectedPack}
        quote={quote}
        submitting={checkoutSubmitting}
        error={checkoutError}
        onQuantityCheckout={onStartCheckout}
        onProceedToPayment={onProceedToPayment}
        onClose={onClosePurchase}
      />
    </Box>
  );
};

export default TopupsPage;
